'use client'

import { format, isValid, parse } from 'date-fns'
import { FC, useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

import { Input } from '../form/input/input'
import { InputWrapper } from '../form/input/input-wrapper'

const DATE_FORMAT = 'MM/dd/yyyy'

type DateRangeValue = {
  start?: Date
  end?: Date
}

type DateRangeSubFilterProps = {
  label: string
  value: DateRangeValue | undefined
  onChange: (value: DateRangeValue) => void
}

const toText = (date: Date | undefined) =>
  date ? format(date, DATE_FORMAT) : ''

type DateBoundInputProps = {
  ariaLabel: string
  date: Date | undefined
  onCommit: (date: Date | undefined) => void
}

// Free text entry for one bound of the range: commits on Enter/blur, clears
// when emptied, and re-syncs whenever the calendar changes the bound.
const DateBoundInput: FC<DateBoundInputProps> = ({
  ariaLabel,
  date,
  onCommit
}) => {
  const [text, setText] = useState(() => toText(date))
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setText(toText(date))
    setHasError(false)
  }, [date])

  const commit = () => {
    const trimmed = text.trim()
    if (!trimmed) {
      setHasError(false)
      onCommit(undefined)
      return
    }
    const parsed = parse(trimmed, DATE_FORMAT, new Date())
    if (isValid(parsed)) {
      setHasError(false)
      onCommit(parsed)
    } else {
      setHasError(true)
    }
  }

  return (
    <InputWrapper inputSize="xs" hasError={hasError} className="grow">
      <Input
        value={text}
        placeholder="MM/DD/YYYY"
        aria-label={ariaLabel}
        hasError={hasError}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            commit()
          }
        }}
      />
    </InputWrapper>
  )
}

export const DateRangeSubFilter: FC<DateRangeSubFilterProps> = ({
  label,
  value,
  onChange
}) => {
  const onDatesChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    onChange({
      start: start ?? undefined,
      end: end ?? undefined
    })
  }

  return (
    <div className="p-2">
      <div className="flex gap-2 px-1 pb-2">
        <DateBoundInput
          ariaLabel={`${label} from date`}
          date={value?.start}
          onCommit={start => onChange({ ...value, start })}
        />
        <DateBoundInput
          ariaLabel={`${label} to date`}
          date={value?.end}
          onCommit={end => onChange({ ...value, end })}
        />
      </div>
      <div className="flex justify-center [&_.react-datepicker]:border-0 [&_.react-datepicker]:shadow-none">
        <DatePicker
          inline
          selectsRange
          selected={value?.start}
          startDate={value?.start}
          endDate={value?.end}
          onChange={onDatesChange}
          aria-label={`Select ${label.toLowerCase()} range`}
        />
      </div>
      {/* Inline pickers render no input container, so react-datepicker's own
          selection announcement never mounts — announce the range ourselves. */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {value?.start
          ? `Selected start date: ${format(value.start, 'PPPP')}.${
              value.end ? ` End date: ${format(value.end, 'PPPP')}` : ''
            }`
          : ''}
      </span>
    </div>
  )
}
